import {
  normalizeDrivingRouteGroupKey,
} from "@utils/mode_tool/modeRoute";
import {
  buildTurnCandidatesFromMapboxRoutes,
  isGeneratedTurnByTurnPoint,
} from "@utils/mode_tool/turnByTurn";

export function splitDirectionsCoordinatesIntoChunks(
  coordinates = [],
  maxCoordinatesPerRequest = 25,
) {
  const safeCoordinates = Array.isArray(coordinates) ? coordinates : [];
  if (safeCoordinates.length <= maxCoordinatesPerRequest) {
    return [safeCoordinates];
  }
  const chunks = [];
  const step = Math.max(2, maxCoordinatesPerRequest - 1);
  for (let startIndex = 0; startIndex < safeCoordinates.length - 1; ) {
    const endIndex = Math.min(
      startIndex + maxCoordinatesPerRequest,
      safeCoordinates.length,
    );
    const chunk = safeCoordinates.slice(startIndex, endIndex);
    if (chunk.length >= 2) {
      chunks.push(chunk);
    }
    if (endIndex >= safeCoordinates.length) break;
    startIndex += step;
  }
  return chunks;
}

export function mergeDirectionsSegmentRoutes(segmentRoutes = []) {
  const validRoutes = (
    Array.isArray(segmentRoutes) ? segmentRoutes : []
  ).filter((route) => Array.isArray(route?.geometry?.coordinates));
  if (!validRoutes.length) return null;
  const mergedCoordinates = [];
  const manualInsertionEdges = [];
  validRoutes.forEach((route, routeIndex) => {
    const coords = Array.isArray(route?.geometry?.coordinates)
      ? route.geometry.coordinates
      : [];
    coords.forEach((coord, coordIndex) => {
      if (
        routeIndex > 0 &&
        coordIndex === 0 &&
        mergedCoordinates.length &&
        mergedCoordinates[mergedCoordinates.length - 1]?.[0] === coord?.[0] &&
        mergedCoordinates[mergedCoordinates.length - 1]?.[1] === coord?.[1]
      ) {
        return;
      }
      if (mergedCoordinates.length) {
        manualInsertionEdges.push(
          coordIndex === 0
            ? route.manualInsertionInterval || null
            : route.geometry.manualInsertionEdges?.[coordIndex - 1] ||
              route.manualInsertionInterval ||
              null,
        );
      }
      mergedCoordinates.push(coord);
    });
  });
  if (mergedCoordinates.length < 2) return null;
  return {
    geometry: {
      type: "LineString",
      coordinates: mergedCoordinates,
      ...(manualInsertionEdges.some(Boolean) ? { manualInsertionEdges } : {}),
    },
    distance: validRoutes.reduce(
      (total, route) => total + Number(route?.distance || 0),
      0,
    ),
    duration: validRoutes.reduce(
      (total, route) => total + Number(route?.duration || 0),
      0,
    ),
    legDurations: validRoutes.flatMap((route) =>
      (Array.isArray(route?.legs) ? route.legs : []).map((leg) =>
        Number(leg?.duration || 0),
      ),
    ),
  };
}

export function buildGeneratedDirectionDurationRows(points = [], routes = []) {
  const directions = (Array.isArray(points) ? points : []).filter(
    (point) =>
      isGeneratedTurnByTurnPoint(point) &&
      normalizeDrivingRouteGroupKey(
        point?.routeGroup ?? point?.route_group ?? "main",
        point?.routeGroupName ?? point?.route_group_name ?? "",
        Number(point?.number_point ?? point?.order ?? 0),
      ) === "main",
  );
  if (!directions.length) return [];

  const candidates = buildTurnCandidatesFromMapboxRoutes(routes, "en")
    .filter((candidate) => Number(candidate?.durationSeconds) > 0)
    .map((candidate) => ({ ...candidate, used: false }));

  return directions.flatMap((point) => {
    const pointLng = Number(point?.longitude ?? point?.lng);
    const pointLat = Number(point?.latitude ?? point?.lat);
    if (!Number.isFinite(pointLng) || !Number.isFinite(pointLat)) return [];
    let closest = null;
    candidates.forEach((candidate) => {
      if (candidate.used) return;
      const candidateLng = Number(candidate?.longitude);
      const candidateLat = Number(candidate?.latitude);
      if (!Number.isFinite(candidateLng) || !Number.isFinite(candidateLat)) return;
      const latitudeScale = 111320;
      const longitudeScale = latitudeScale * Math.cos((pointLat * Math.PI) / 180);
      const distanceMeters = Math.hypot(
        (pointLng - candidateLng) * longitudeScale,
        (pointLat - candidateLat) * latitudeScale,
      );
      if (!closest || distanceMeters < closest.distanceMeters) {
        closest = { candidate, distanceMeters };
      }
    });
    if (!closest || closest.distanceMeters > 100) return [];
    closest.candidate.used = true;
    return [{
      pointId: String(point?.id || ""),
      durationSeconds: Number(closest.candidate.durationSeconds),
    }];
  });
}

export function reduceDrivingPointsToRoutingAnchors(pointsList = []) {
  const safePoints = [...(Array.isArray(pointsList) ? pointsList : [])].sort(
    (a, b) =>
      Number(a?.number_point ?? a?.order ?? 0) -
      Number(b?.number_point ?? b?.order ?? 0),
  );
  if (safePoints.length <= 2) return safePoints;
  return safePoints.filter((point, index, list) => {
    const lng = Number(point?.longitude ?? point?.lng);
    const lat = Number(point?.latitude ?? point?.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    if (index === 0) return true;
    const previousPoint = list[index - 1];
    const previousLng = Number(
      previousPoint?.longitude ?? previousPoint?.lng,
    );
    const previousLat = Number(previousPoint?.latitude ?? previousPoint?.lat);
    return lng !== previousLng || lat !== previousLat;
  });
}

export function getRoutePointSequenceValue(point = {}, fallbackIndex = 0) {
  return (
    Number(point?.order ?? point?.number_point ?? fallbackIndex + 1) ||
    fallbackIndex + 1
  );
}

export function buildDrivingRouteGroupsForDirections(pointsList = []) {
  const sortedPoints = [
    ...(Array.isArray(pointsList) ? pointsList : []).filter(
      (point) => !isGeneratedTurnByTurnPoint(point),
    ),
  ].sort(
    (a, b) => getRoutePointSequenceValue(a) - getRoutePointSequenceValue(b),
  );
  const resolvePointIdentity = (point = {}, fallbackIndex = 0) =>
    String(
      point?.id ||
        `${getRoutePointSequenceValue(point, fallbackIndex)}:${Number(
          point?.longitude ?? point?.lng ?? 0,
        ).toFixed(7)}:${Number(point?.latitude ?? point?.lat ?? 0).toFixed(7)}`,
    );
  const uniqueRoutePoints = (points = []) => {
    const seen = new Set();
    return (Array.isArray(points) ? points : []).filter((point, index) => {
      if (!point) return false;
      const identity = resolvePointIdentity(point, index);
      if (seen.has(identity)) return false;
      seen.add(identity);
      return true;
    });
  };
  const mainPoints = sortedPoints.filter(
    (point) =>
      normalizeDrivingRouteGroupKey(
        point?.routeGroup ?? point?.route_group ?? "",
        point?.routeGroupName ?? point?.route_group_name ?? "",
        getRoutePointSequenceValue(point),
      ) === "main",
  );
  const routeGroups = [];
  if (mainPoints.length >= 2) {
    routeGroups.push({
      routeGroup: "main",
      points: mainPoints,
    });
  }

  let currentAlternateKey = "";
  let currentAlternatePoints = [];

  const flushAlternateGroup = () => {
    if (!currentAlternateKey || currentAlternatePoints.length < 1) return;
    const uniqueBranchPoints = uniqueRoutePoints(currentAlternatePoints);
    if (uniqueBranchPoints.length >= 2) {
      routeGroups.push({
        routeGroup: currentAlternateKey,
        points: uniqueBranchPoints,
      });
    }
    currentAlternateKey = "";
    currentAlternatePoints = [];
  };

  sortedPoints.forEach((point) => {
    const routeGroup = normalizeDrivingRouteGroupKey(
      point?.routeGroup ?? point?.route_group ?? "",
      point?.routeGroupName ?? point?.route_group_name ?? "",
      getRoutePointSequenceValue(point),
    );
    if (routeGroup === "main") {
      flushAlternateGroup();
      return;
    }
    if (!currentAlternateKey) {
      currentAlternateKey = routeGroup;
      currentAlternatePoints = [point];
      return;
    }
    if (currentAlternateKey !== routeGroup) {
      flushAlternateGroup();
      currentAlternateKey = routeGroup;
      currentAlternatePoints = [point];
      return;
    }
    currentAlternatePoints.push(point);
  });

  flushAlternateGroup();
  return routeGroups;
}
